import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, Star, Quote, TrendingUp, Fuel, Wrench, Trophy, ArrowRight, Check } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const testimonials = [
  {
    id: 1,
    name: "Michael R.",
    location: "Queensland, Australia",
    vehicle: "2019 BMW 340i (B58)",
    service: "Stage 1+ Tune",
    rating: 5,
    beforeAfter: {
      power: { before: "298 WHP", after: "382 WHP", gain: "+84 HP" },
      torque: { before: "328 lb-ft", after: "461 lb-ft", gain: "+133 lb-ft" }
    },
    testimonial: "I was sceptical, honestly. But the difference is night and day. The car pulls like a train now and it's still perfectly civilised for my daily commute. Throttle response alone was worth the money - no more waiting for the turbo to wake up. Took it to a track day last month and kept up with cars that cost twice what mine did.",
    highlight: "Kept up with cars costing twice as much",
    category: "Performance"
  },
  {
    id: 2,
    name: "Transport Solutions Ltd",
    location: "Auckland, New Zealand",
    vehicle: "Fleet of 12x Mercedes Sprinter 314 CDI",
    service: "Economy Tune + EGR Delete",
    rating: 5,
    beforeAfter: {
      consumption: { before: "11.2 L/100km", after: "9.6 L/100km", gain: "-14%" },
      annualSavings: "$33,600 fleet savings"
    },
    testimonial: "I'll admit, the board was sceptical when I proposed ECU tuning for the fleet. But the fuel savings spoke for themselves. The drivers actually prefer the tuned vans now - they say they're more responsive and easier to drive fully loaded. Wish we'd done it sooner.",
    highlight: "$33,600 annual fleet savings",
    category: "Commercial"
  },
  {
    id: 3,
    name: "Robert M.",
    location: "Western Australia",
    vehicle: "2017 Toyota Land Cruiser 200 V8 Diesel",
    service: "DPF Delete + Stage 1",
    rating: 5,
    beforeAfter: {
      power: { before: "272 HP", after: "320 HP", gain: "+48 HP" },
      torque: { before: "650 Nm", after: "780 Nm", gain: "+130 Nm" },
      dpfProblems: { before: "Constant", after: "Eliminated" }
    },
    testimonial: "Kicking myself for not doing this sooner. No more warning lights every other week, no more trips to the Toyota dealer, and the thing pulls our gear like it's nothing now. It's a completely different vehicle - actually enjoy driving it instead of dreading the next DPF warning.",
    highlight: "Saved $6,000 on DPF replacement",
    category: "Agricultural"
  },
  {
    id: 4,
    name: "James T.",
    location: "Victoria, Australia",
    vehicle: "2019 VW Golf GTI Mk7.5",
    service: "Stage 1 ECU + DSG Tune",
    rating: 5,
    beforeAfter: {
      power: { before: "245 HP", after: "310 HP", gain: "+65 HP" },
      torque: { before: "370 Nm", after: "450 Nm", gain: "+80 Nm" }
    },
    testimonial: "Can't believe the difference from just software. The DSG tune alone transformed the car - shifts are instant now, none of that annoying hesitation. Combined with the extra power, it genuinely feels like a different car. Mate with an R32 couldn't believe it when I pulled past him on the M1!",
    highlight: "Beat an R32 with just a tune",
    category: "Performance"
  },
  {
    id: 5,
    name: "Emma S.",
    location: "New South Wales, Australia",
    vehicle: "2016 Peugeot 308 1.6 HDi",
    service: "Economy Stage 1",
    rating: 5,
    beforeAfter: {
      consumption: { before: "5.8 L/100km", after: "4.9 L/100km", gain: "-15%" },
      annualSavings: "$1,020 annual fuel savings"
    },
    testimonial: "I was just hoping for 10% honestly - getting 15% was a pleasant surprise. And the car actually drives better too. More responsive, overtaking is easier, and I'm spending noticeably less at the pump. Worked out the tune paid for itself in about 4 months. Happy with that.",
    highlight: "Tune paid for itself in 4 months",
    category: "Economy"
  },
  {
    id: 6,
    name: "David K.",
    location: "South Australia",
    vehicle: "2015 BMW F30 335d",
    service: "Stage 2 + Full Delete Package",
    rating: 5,
    beforeAfter: {
      power: { before: "313 HP", after: "420 HP", gain: "+107 HP" },
      torque: { before: "630 Nm", after: "780 Nm", gain: "+150 Nm" },
      quarterMile: { before: "13.2 sec", after: "12.1 sec", gain: "-1.1 sec" }
    },
    testimonial: "This thing is an absolute weapon now. 780 Nm of torque in a sedan - nothing lives with it off the line. I've embarrassed a few sports cars at the lights and they probably still can't figure out what happened. Best part? Looks completely stock, gets decent fuel economy, comfortable for long trips. The perfect sleeper.",
    highlight: "780 Nm torque - ultimate sleeper",
    category: "Performance"
  },
  {
    id: 7,
    name: "Sarah & Tom's Farm",
    location: "Canterbury, New Zealand",
    vehicle: "2018 Ford Ranger 3.2 TDCi",
    service: "DPF + EGR Delete",
    rating: 5,
    beforeAfter: {
      reliability: { before: "DPF issues monthly", after: "Zero problems in 18 months" },
      power: { before: "200 HP", after: "240 HP", gain: "+40 HP" }
    },
    testimonial: "Farm trucks don't get highway miles, full stop. So our DPF was blocking up every few months. We'd already spent a fortune on forced regens at the dealer. Since the delete, haven't had a single issue - eighteen months and counting. Extra power is great for towing too. Should've done this from day one.",
    highlight: "18 months problem-free",
    category: "Agricultural"
  },
  {
    id: 8,
    name: "Chris P.",
    location: "Wellington, New Zealand",
    vehicle: "2020 Audi S3 (EA888)",
    service: "Stage 2 Tune",
    rating: 5,
    beforeAfter: {
      power: { before: "310 HP", after: "420 HP", gain: "+110 HP" },
      torque: { before: "400 Nm", after: "520 Nm", gain: "+120 Nm" }
    },
    testimonial: "Fitted the downpipe and intake, then got the Stage 2 tune done. Completely woke the car up. Measured 0-100 at 3.8 seconds - that's quicker than a stock RS3 for a fraction of the cost. The team at ECU Flash clearly know their stuff. Quick turnaround, good communication, no dramas.",
    highlight: "Faster than RS3, fraction of cost",
    category: "Performance"
  }
];

const stats = [
  { label: "Happy Customers", value: "10,000+", icon: Trophy },
  { label: "Files Processed", value: "50,000+", icon: Wrench },
  { label: "Average Rating", value: "4.9/5", icon: Star },
  { label: "Countries Served", value: "50+", icon: TrendingUp }
];

const TestimonialsPage = () => {
  useCanonical('https://ecuflashservice.com/testimonials');

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "ECU Flash Service",
    "image": "https://ecuflashservice.com/favicon-512.png",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "847",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": testimonials.slice(0, 5).map(t => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": t.name },
      "reviewRating": { "@type": "Rating", "ratingValue": t.rating },
      "reviewBody": t.testimonial
    }))
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Customer Testimonials & Success Stories | ECU Tuning Results | ECU Flash Service</title>
        <meta name="description" content="Read real customer reviews and success stories. See actual before/after tuning results from BMW, Mercedes, VW, Audi, Ford owners. 4.9/5 rating from 800+ reviews." />
        <meta name="keywords" content="ECU tuning reviews, remap testimonials, tuning results, customer success stories, ECU Flash Service reviews" />
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</span>
            </Link>
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
              <span>Get Started</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <Star className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Customer Reviews</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Success</span>{' '}
            <span className="text-[#00F0FF]">Stories</span>
          </h1>
          <p className="text-[#888] text-lg mb-8 max-w-2xl mx-auto">
            Don't take our word for it. Here's what our customers have to say about their tuning results - with real numbers to back it up.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-[#0A0A0A] border border-white/10 p-4">
                <stat.icon className="w-6 h-6 text-[#00F0FF] mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-[#888] uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-[#0A0A0A] border border-white/10 hover:border-[#00F0FF]/30 transition overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">{testimonial.name}</h3>
                      <p className="text-sm text-[#888]">{testimonial.location}</p>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-[#FFD700] fill-current" />
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-[#1A1A1A] border border-white/10 text-xs text-[#00F0FF]">
                      {testimonial.vehicle}
                    </span>
                    <span className="px-2 py-1 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-xs text-[#00F0FF]">
                      {testimonial.service}
                    </span>
                  </div>
                </div>

                {/* Results */}
                <div className="p-6 bg-[#080808]">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#00F0FF] mb-3">Results</h4>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {testimonial.beforeAfter.power && (
                      <div className="bg-[#0A0A0A] p-3 border border-white/5">
                        <div className="text-xs text-[#888] mb-1">Power</div>
                        <div className="text-sm text-white">{testimonial.beforeAfter.power.before} → {testimonial.beforeAfter.power.after}</div>
                        <div className="text-xs text-[#00FF88] font-bold">{testimonial.beforeAfter.power.gain}</div>
                      </div>
                    )}
                    {testimonial.beforeAfter.torque && (
                      <div className="bg-[#0A0A0A] p-3 border border-white/5">
                        <div className="text-xs text-[#888] mb-1">Torque</div>
                        <div className="text-sm text-white">{testimonial.beforeAfter.torque.before} → {testimonial.beforeAfter.torque.after}</div>
                        <div className="text-xs text-[#00FF88] font-bold">{testimonial.beforeAfter.torque.gain}</div>
                      </div>
                    )}
                    {testimonial.beforeAfter.consumption && (
                      <div className="bg-[#0A0A0A] p-3 border border-white/5">
                        <div className="text-xs text-[#888] mb-1">Fuel</div>
                        <div className="text-sm text-white">{testimonial.beforeAfter.consumption.before} → {testimonial.beforeAfter.consumption.after}</div>
                        <div className="text-xs text-[#00FF88] font-bold">{testimonial.beforeAfter.consumption.gain}</div>
                      </div>
                    )}
                    {testimonial.beforeAfter.annualSavings && (
                      <div className="bg-[#0A0A0A] p-3 border border-white/5">
                        <div className="text-xs text-[#888] mb-1">Savings</div>
                        <div className="text-sm text-[#00FF88] font-bold">{testimonial.beforeAfter.annualSavings}</div>
                      </div>
                    )}
                  </div>

                  {/* Highlight */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#00F0FF]/5 border border-[#00F0FF]/20 mb-4">
                    <Trophy className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-sm text-[#00F0FF] font-semibold">{testimonial.highlight}</span>
                  </div>

                  {/* Quote */}
                  <div className="relative">
                    <Quote className="absolute -top-1 -left-1 w-6 h-6 text-[#00F0FF]/20" />
                    <p className="text-sm text-[#888] leading-relaxed pl-5 italic">
                      "{testimonial.testimonial}"
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-8 text-center">
            Why People Come Back
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Quick Turnaround", desc: "Most files back in your inbox within 20-60 minutes" },
              { title: "We Know Our Stuff", desc: "Years of hands-on experience with every major ECU platform" },
              { title: "Money-Back Guarantee", desc: "Not happy with the result? You get your money back. Simple" },
              { title: "Free Revisions", desc: "If something's not right with the file, we fix it at no charge" },
              { title: "Your File, Your Vehicle", desc: "Every tune is calibrated specifically for your ECU file, not a generic map" },
              { title: "Worldwide", desc: "We've shipped files to 50+ countries. Time zones don't stop us" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-sm text-[#888]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
            Want Results Like These?
          </h2>
          <p className="text-[#888] mb-6">Upload your ECU file and tell us what you're after. We'll take care of the rest.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition inline-flex items-center justify-center gap-2">
              <span>Start Your Order</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-skew border border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-[#00F0FF] transition">
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;
